// Copyright 2025, University of Maryland, All Rights Reserved
// Local development authentication setup - bypasses CAS redirect for local dev

/**
 * Pre-seed localStorage with token from local auth endpoint.
 *
 * Uses an explicit VITE_LOCAL_AUTH flag instead of inferring local mode from
 * VITE_CAS_LOGIN_URL being empty. This avoids Vite env file precedence issues
 * where .env.local (real CAS URL) overrides .env.development.local (empty URL).
 *
 * Set by local-dev-all.sh or CI e2e jobs.
 */
export async function setupLocalAuth(): Promise<void> {
  // Never run in production builds — defense against leaked env vars
  if (import.meta.env.PROD) {
    return
  }

  const localAuthUrl = import.meta.env.VITE_CAS_API_URL
  const isLocalAuth = import.meta.env.VITE_LOCAL_AUTH === 'true'

  if (!isLocalAuth || !localAuthUrl) {
    return
  }

  // Clear stale auth state - backend restarts invalidate old tokens and cause loading hangs
  localStorage.removeItem('cas_jwt')
  localStorage.removeItem('cas_user')

  console.log('[Local Auth] Fetching fresh token from:', localAuthUrl)

  try {
    const response = await fetch(localAuthUrl)
    const data = await response.json()

    if (data.token) {
      localStorage.setItem('cas_jwt', data.token)
      localStorage.setItem('cas_user', JSON.stringify({
        token: data.token,
        username: data.username,
        name: data.name,
        email: data.email,
      }))
      console.log('[Local Auth] Token stored for user:', data.username)
    }
  } catch (error) {
    console.error('[Local Auth] Failed to fetch token:', error)
  }
}
